import * as UI from "@iyk/music-ui"
import { AppLink } from "@iyk/react-router/client"
import { Data } from "effect"
import { AnimatePresence, motion } from "motion/react"
import * as React from "react"
import { Link } from "react-router"
import { AppRoutes } from "../AppRoutes.ts"
import { Meta } from "../ui/components/Meta.tsx"

type Media = Data.TaggedEnum<{
  Image: {
    src: string
    alt: string
    id: string
    caption?: string
    width: number
    height: number
  }
  Video: {
    posterUrl: string
    playbackUrl: string
    id: string
    caption?: string
    width: number
    height: number
  }
}>
const Media = Data.taggedEnum<Media>()

export function MarketingLandingPage({ joinWaitlistUrl }: { joinWaitlistUrl: string }) {
  return (
    <div
      className="dark text-gray-12 bg-[var(--bg-color)]"
      style={{ "--bg-color": "#070707" } as React.CSSProperties}
    >
      <Meta />
      <header className="sticky top-0 left-0 w-full p-4 z-40 flex items-center justify-between bg-[var(--bg-color)]">
        <AppLink to={AppRoutes.home}>
          <UI.Icon.IykLogo className="size-7" />
        </AppLink>

        <AppLink to={AppRoutes.login()} className={UI.classForButton({ kind: "shaded", size: "sm" })}>
          Login
        </AppLink>
      </header>

      <HeroSection joinWaitlistUrl={joinWaitlistUrl} />

      {/** Main Content */}
      <motion.main
        className="flex flex-col gap-0"
        initial="hidden"
        animate="show"
        variants={{
          hidden: { opacity: 0 },
          show: { opacity: 1 }
        }}
        transition={{
          duration: 1.5,
          delay: 1,
          type: "tween",
          ease: "easeInOut"
        }}
      >
        <Section
          align="center"
          title={
            <span className="block max-w-[300px] sm:max-w-[500px] mx-auto text-pretty">
              Strengthen the artist-fan relationship and build a core that shows up again and again
            </span>
          }
        >
          <div className="relative z-10 max-w-[780px] mx-auto sm:mt-12 w-full text-left">
            <FlywheelSvg className="hidden sm:block absolute h-[112%] top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-10 z-10 relative">
              {Content.flywheel.map((step) => {
                const isStep3 = step.step === 3

                return (
                  <div
                    className={UI.cva(
                      [
                        "p-5 flex flex-col justify-between aspect-square bg-gray-1 rounded-xl sm:rounded-2xl bg-cover bg-center bg-no-repeat",
                        classForCardBorder,
                        "after:rounded-xl sm:after:rounded-2xl"
                      ],
                      {
                        variants: {
                          isStep3: {
                            true: "sm:order-4"
                          }
                        }
                      }
                    )({ isStep3 })}
                    style={{ backgroundImage: `url(${step.image})` }}
                    key={step.step}
                  >
                    <div className="flex flex-col gap-1.5">
                      <span className="size-5 flex items-center justify-center bg-white text-black rounded-sm text-xs uppercase font-departure-mono">
                        {step.step}
                      </span>
                      <h3 className="text-lg">{step.title}</h3>
                    </div>
                    <p className="w-full text-sm sm:text-xs md:text-sm sm:max-w-[240px] sm:text-balance">
                      {step.description}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>
        </Section>

        <Section title="Core Features">
          <div className="flex flex-col gap-2">
            {Content.coreFeatures.map((feature, index) => (
              <CoreFeature
                key={index}
                title={feature.title}
                description={feature.description}
                pill={feature.pill}
                desktopImage={feature.desktopImage}
                mobileImage={feature.mobileImage}
              />
            ))}
          </div>
        </Section>

        <Section sidePadding={false}>
          <div className="w-full">
            <div
              className="aspect-[4320/2139] bg-contain bg-center bg-no-repeat text-center"
              style={{ backgroundImage: `url(${Assets.subFeatures.buildYourWorld})` }}
            >
              <h2 className="text-xl sm:text-2xl">
                Build Your World
                <br /> with IYK Music
              </h2>
              <img
                role="presentation"
                src={Assets.subFeatures.buildYourWorldMobile}
                alt="Build Your World"
                className="w-full md:hidden"
              />
            </div>

            <div className="grid sm:hidden xl:grid grid-cols-1 xl:grid-cols-4 gap-2 px-4 xl:px-8">
              {Content.subFeatures.map((feature) => (
                <SubFeature
                  title={feature.title}
                  description={feature.description}
                  image={feature.image}
                  isComingSoon={feature.isComingSoon}
                  key={feature.title}
                />
              ))}
            </div>

            <UI.Carousel.Root
              className="hidden sm:block xl:hidden"
              slideSize={{
                base: "90%",
                sm: "70%",
                md: "45%",
                lg: "30%",
                xl: "25%"
              }}
              slideSpacing={8}
            >
              <UI.Carousel.Slides
                slides={Content.subFeatures}
                classNames={{ viewport: "w-full overflow-hidden [contain:inline-size] px-4 sm:px-8" }}
              >
                {(feature) => (
                  <UI.Carousel.Slide>
                    <SubFeature
                      title={feature.title}
                      description={feature.description}
                      image={feature.image}
                      isComingSoon={feature.isComingSoon}
                    />
                  </UI.Carousel.Slide>
                )}
              </UI.Carousel.Slides>
            </UI.Carousel.Root>
          </div>
        </Section>

        <Section sidePadding={false}>
          <UI.Carousel.Root slideSize={{ base: "90%" }}>
            <div className="flex items-center justify-between px-4 sm:px-8 mb-8">
              <h2 className="text-xl sm:text-2xl">Artist Case Studies</h2>

              <CarouselControls />
            </div>

            <UI.Carousel.Slides
              slides={Content.playbookStories}
              classNames={{ viewport: "px-4 sm:px-8 rounded overflow-hidden" }}
            >
              {(caseStudy) => <CaseStudySlide caseStudy={caseStudy} joinWaitlistUrl={joinWaitlistUrl} />}
            </UI.Carousel.Slides>
          </UI.Carousel.Root>
        </Section>

        <Section
          sidePadding={false}
          align="center"
          title={
            <span className="text-center">
              Trusted by global icons
              <br /> and the next generation of artists
            </span>
          }
        >
          <div className="w-full mt-8 2xl:px-8">
            <UI.Carousel.Root
              className="relative rounded overflow-hidden"
              slideSize={{
                base: "50%",
                sm: "33%",
                md: "12.5%",
                lg: "12.5%",
                xl: "12.5%"
              }}
              slideSpacing={8}
              options={{
                loop: true,
                skipSnaps: true,
                autoScroll: {
                  active: true,
                  stopOnInteraction: false,
                  playOnInit: true,
                  startDelay: 0,
                  speed: 0.1
                }
              }}
            >
              <UI.Carousel.Slides
                slides={Assets.artistMarquee}
                classNames={{ viewport: "w-full overflow-hidden [contain:inline-size]" }}
              >
                {(data) => (
                  <UI.Carousel.Slide className="min-w-[144px] sm:min-w-[225px]" key={data}>
                    <img
                      loading="lazy"
                      className={UI.cx("h-full aspect-[7/9] rounded", classForCardBorder, "after:rounded")}
                      src={data}
                      alt="Artist using IYK"
                    />
                  </UI.Carousel.Slide>
                )}
              </UI.Carousel.Slides>
            </UI.Carousel.Root>
          </div>
        </Section>

        <Section>
          <div className="flex flex-col gap-11">
            <div className="flex flex-col gap-4">
              <div
                style={{ backgroundImage: `url(${Assets.shop.banner})` }}
                className="relative overflow-hidden aspect-[4/3] md:aspect-[16/6] bg-cover bg-center rounded-xl flex flex-col gap-2 justify-end p-5 sm:px-10 sm:py-11"
              >
                <div className="absolute bottom-0 left-0 w-full h-full lg:h-1/2 bg-gradient-to-b from-transparent to-gray-1" />
                <div className="flex flex-col items-start gap-2 z-10">
                  <div className="flex flex-col gap-1">
                    <h2 className="text-lg">IYK Shop</h2>
                    <div className="relative max-w-[488px] w-full">
                      <p className="relative sm:text-lg text-gray-10 z-10">
                        Start with merch that remembers who showed up — turning physical products into lasting
                        proof of fandom.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                {Content.shop.products.map((product) => (
                  <div
                    key={product.title}
                    className={UI.cx(
                      "aspect-square bg-gray-1 rounded-xl flex flex-col",
                      product.hideOnMobile && "hidden sm:flex"
                    )}
                  >
                    <div className="grow flex items-center justify-center p-6">
                      <img
                        loading="lazy"
                        src={product.image}
                        alt={product.title}
                        className="size-full object-contain"
                      />
                    </div>
                    <div className="shrink-0 h-11 flex items-center justify-center px-3 pb-4">
                      <span className="text-xs-departure !text-[11px] text-center">{product.title}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>
      </motion.main>

      {/** Footer */}
      <footer className="sm:bg-gray-1">
        <div className="max-w-screen-2xl w-full mx-auto px-4 sm:px-8 py-10 sm:pt-14 sm:pb-6">
          <div className="flex flex-col items-start justify-start gap-4 sm:gap-6 pb-32 border-b border-gray-4 sm:border-none">
            <h3 className="text-xl text-balance max-w-[400px]">
              Built to make every real-world action visible, valuable, and verifiable.
            </h3>
            <Link to={joinWaitlistUrl} className={UI.classForButton({ kind: "solid", size: "md" })}>
              Join as an artist
            </Link>
          </div>

          <div className="flex items-start justify-between text-xs pt-6">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-5">
              <FooterLink href={Content.footer.urls.twitter} title="X (Twitter)" />
              <FooterLink href={Content.footer.urls.instagram} title="Instagram" />
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-5">
              <FooterLink href={Content.footer.urls.privacyPolicy} title="Privacy Policy" />
              <FooterLink href={Content.footer.urls.termsOfService} title="Terms of Service" />
              <Copyright className="hidden sm:block" />
            </div>
          </div>

          <Copyright className="block sm:hidden mt-20" />
        </div>
      </footer>
    </div>
  )
}

const HeroSection = ({ joinWaitlistUrl }: { joinWaitlistUrl: string }) => {
  const [activeIndex, setActiveIndex] = React.useState(0)
  const activeImage = Assets.doubledFanPasses[activeIndex]

  return (
    <div className="relative h-[85vh] max-h-[1200px]">
      <div className="flex flex-col items-center justify-center text-center gap-4 h-1/2">
        <div className="flex flex-col gap-2">
          <motion.h1
            initial="hidden"
            animate="show"
            variants={{
              hidden: { opacity: 0, filter: "blur(1px)" },
              show: { opacity: 1, filter: "blur(0px)" }
            }}
            transition={{ duration: 1, ease: "easeInOut" }}
            className="will-change-transform text-xs sm:text-sm"
          >
            IYK Music
          </motion.h1>
          <HeroStaggeredText show={true} />
          <motion.p
            initial="hidden"
            animate="show"
            variants={{
              hidden: { opacity: 0, y: 2, filter: "blur(1px)" },
              show: { opacity: 1, y: 0, filter: "blur(0px)" }
            }}
            transition={{ duration: 1, delay: 0.5, ease: "easeInOut" }}
            className="will-change-transform text-sm sm:text-base max-w-[260px] sm:max-w-[480px] mx-auto text-pretty"
          >
            Build lasting fan connections through merch and live shows. Identify your top fans and reward
            them.
          </motion.p>
        </div>
        <motion.div
          className="will-change-transform"
          initial="hidden"
          animate="show"
          variants={{
            hidden: { opacity: 0, y: 2, filter: "blur(1)" },
            show: { opacity: 1, y: 0, filter: "blur(0px)" }
          }}
          transition={{ duration: 1, delay: 0.8, ease: "easeInOut" }}
        >
          <Link
            to={joinWaitlistUrl}
            className={UI.classForButton({ kind: "solid", size: "md", className: "min-w-20" })}
          >
            Join as an artist
          </Link>
        </motion.div>
      </div>

      <motion.div
        initial="hidden"
        animate="show"
        variants={{
          hidden: { opacity: 0, y: 2, filter: "blur(1)" },
          show: { opacity: 1, y: 0, filter: "blur(0px)" }
        }}
        transition={{ duration: 1, delay: 1, ease: "easeInOut" }}
        className="relative h-1/2 flex items-center w-full"
      >
        <UI.Carousel.Root
          className="overflow-hidden w-full"
          onSlideChange={setActiveIndex}
          slideSize="calc(20vh)"
          slideSpacing={8}
          options={{
            loop: true,
            skipSnaps: true,
            autoPlay: {
              active: true,
              stopOnInteraction: false,
              playOnInit: true,
              delay: 3000
            }
          }}
        >
          <UI.Carousel.Slides
            slides={Assets.doubledFanPasses}
            classNames={{ viewport: "w-full overflow-hidden [contain:inline-size]" }}
          >
            {(data) => (
              <UI.Carousel.Slide className="" key={data}>
                <img className="size-full object-contain" src={data} alt="World ID" />
              </UI.Carousel.Slide>
            )}
          </UI.Carousel.Slides>
        </UI.Carousel.Root>

        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-full aspect-[780/976] pointer-events-none">
          <img
            className="relative z-10 size-full object-contain select-none"
            src={Assets.iphoneFrame}
            alt="iphone frame"
          />
          <div className="overflow-hidden absolute bottom-0 left-1/2 -translate-x-1/2 w-[82%] h-[95%] rounded-t-[13%] bg-gray-1">
            <AnimatePresence>
              {Assets.doubledFanPasses.map((image, index) => {
                const isActive = image === activeImage

                return (
                  <React.Fragment key={index}>
                    <motion.img
                      key={`${index}-blur`}
                      variants={{
                        active: {
                          opacity: 1
                        },
                        inactive: {
                          opacity: 0
                        }
                      }}
                      initial="inactive"
                      animate={isActive ? "active" : "inactive"}
                      exit="inactive"
                      src={image}
                      className="scale-[2] absolute top-[-10%] left-0 right-0 origin-top blur-xl opacity-50"
                    />
                    <motion.img
                      key={`${index}-image`}
                      variants={{
                        active: {
                          opacity: 1,
                          scale: 1,
                          y: "-50%",
                          x: "-50%"
                        },
                        inactive: {
                          opacity: 0,
                          scale: 0.96,
                          y: "-45%",
                          x: "-50%"
                        }
                      }}
                      initial="inactive"
                      animate={isActive ? "active" : "inactive"}
                      exit="inactive"
                      transition={{ duration: 0.5, type: "spring" }}
                      src={image}
                      className="absolute top-1/2 h-[70%] left-1/2 z-10"
                    />
                  </React.Fragment>
                )
              })}
            </AnimatePresence>
          </div>
        </div>

        <div className="z-10 absolute -bottom-px left-0 right-0 h-1/3 bg-gradient-to-b from-transparent to-[var(--bg-color)] to-85% pointer-events-none" />
        <div className="z-10 absolute inset-0 bg-gradient-to-r from-[var(--bg-color)]/60 via-transparent to-[var(--bg-color)]/60 pointer-events-none" />
      </motion.div>
    </div>
  )
}

const HeroStaggeredText = ({ show }: { show: boolean }) => {
  const text = "Recognize the fans who show up"
  const breakAtChar = 19
  const letters = text.split("")

  return (
    <motion.h2
      viewport={{ once: true }}
      className={UI.cx("text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-center")}
    >
      {letters.map((letter, i) => {
        const key = `${letter}-${i}`

        return (
          <React.Fragment key={key}>
            {i === breakAtChar && <br />}
            <motion.span
              className="inline-block"
              key={key}
              initial="hidden"
              animate={show ? "show" : "hidden"}
              variants={{
                hidden: { opacity: 0, y: 4, filter: "blur(3px)" },
                show: (i: number) => ({
                  opacity: 1,
                  y: 0,
                  filter: "blur(0px)",
                  transition: { delay: 0.25 + i * 0.02 }
                })
              }}
              custom={i}
            >
              {letter === " " ? "\u00A0" : letter}
            </motion.span>
          </React.Fragment>
        )
      })}
    </motion.h2>
  )
}

const CoreFeature = ({
  title,
  description,
  pill,
  desktopImage,
  mobileImage
}: {
  title: string
  description: string
  pill: string
  desktopImage: string
  mobileImage: string
}) => {
  return (
    <div
      className={UI.cx(
        "bg-[var(--bg-color)] grid grid-cols-1 md:grid-cols-2 rounded-xl overflow-hidden bg-cover bg-center",
        classForCardBorder,
        "after:rounded-xl"
      )}
      style={{ backgroundImage: `url(${Assets.coreFeatures.gridBg})` }}
    >
      <div className="flex flex-col justify-center items-start gap-6 px-4 pt-0.5 pb-10 md:p-10 lg:p-20 order-1 md:-order-1">
        <span className="flex items-center justify-center absolute top-6 left-6 md:static h-6 px-2 text-xs-departure rounded-md leading-none bg-gray-12 text-gray-1 shadow-xl">
          {pill}
        </span>
        <div className="flex flex-col gap-2">
          <h2 className="text-lg">{title}</h2>
          <p className="text-sm text-pretty max-w-[450px]">{description}</p>
        </div>
      </div>

      <picture className="size-full">
        <source media="(min-width: 640px)" srcSet={desktopImage} />
        <img className="size-full object-cover" src={mobileImage} alt={title} />
      </picture>
    </div>
  )
}

const SubFeature = ({
  title,
  description,
  image,
  isComingSoon
}: {
  title: string
  description: string
  image: string
  isComingSoon: boolean
}) => {
  return (
    <div
      className={UI.cx(
        "overflow-hidden relative p-6 rounded-xl aspect-[3/4] h-full bg-cover bg-center flex flex-col justify-between",
        classForCardBorder,
        "after:rounded-xl"
      )}
      style={{ backgroundImage: `url(${image})` }}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs-departure">{title}</h3>
        {isComingSoon && <Badge>Coming soon</Badge>}
      </div>
      <p className="text-sm text-pretty pr-12">{description}</p>
    </div>
  )
}

const CarouselControls = () => {
  const { goToPreviousSlide, goToNextSlide, canGoToNextSlide, canGoToPreviousSlide } =
    UI.Carousel.useCarousel()

  return (
    <div className="hidden sm:flex items-center gap-2">
      <button
        className={classForCarouselControlButton({ isDisabled: !canGoToPreviousSlide() })}
        type="button"
        onClick={goToPreviousSlide}
      >
        <UI.Icon.ChevronLeft />
      </button>

      <button
        className={classForCarouselControlButton({ isDisabled: !canGoToNextSlide() })}
        type="button"
        onClick={goToNextSlide}
      >
        <UI.Icon.ChevronRight />
      </button>
    </div>
  )
}
const classForCarouselControlButton = UI.cva(
  [
    "flex items-center justify-center",
    "size-11 rounded-full bg-gray-1 text-gray-12 transition-opacity duration-300"
  ],
  {
    variants: {
      isDisabled: {
        true: "opacity-50 cursor-not-allowed"
      }
    }
  }
)

const Section = ({
  children,
  title,
  align = "stretch",
  sidePadding = true
}: {
  children: React.ReactNode
  title?: string | React.ReactNode
  align?: "stretch" | "center"
  sidePadding?: boolean
}) => {
  return (
    <section
      className={UI.cva(
        ["py-8 flex flex-col gap-8 min-w-0 overflow-hidden max-w-screen-2xl mx-auto w-full"],
        {
          variants: {
            align: {
              stretch: "items-stretch",
              center: "items-center text-center"
            },
            sidePadding: {
              true: "px-4 sm:px-8",
              false: "px-0"
            }
          }
        }
      )({ align, sidePadding })}
    >
      {title && (
        <h2
          className={UI.cva(["text-xl sm:text-2xl"], {
            variants: { sidePadding: { true: "px-0", false: "px-4 sm:px-8" } }
          })({ sidePadding })}
        >
          {title}
        </h2>
      )}

      {children}
    </section>
  )
}

const Copyright = ({ className }: { className?: string }) => {
  const year = new Date().getFullYear()

  return <span className={UI.cx("text-xs text-gray-11", className)}>&copy; IYK {year}</span>
}

const MediaComponent = ({ media, className }: { media: Media; className?: string }) => {
  const aspectRatio = media.width / media.height
  const style = {
    aspectRatio: `${aspectRatio}`
  }
  const baseClassName = UI.cx("size-full border border-gray-3 rounded-xl flex overflow-hidden")

  return (
    <figure className="flex flex-col gap-6">
      {Media.$match(media, {
        Image: (m) => (
          <img className={UI.cx(baseClassName, className)} style={style} src={m.src} alt={m.alt} />
        ),
        Video: (m) => (
          <UI.VideoPlayer
            style={style}
            className={UI.cx(baseClassName, className)}
            id={m.id}
            src={m.playbackUrl}
            poster={m.posterUrl}
            size="none"
            kind="minimal"
            playsInline
            autoPlay
            loop
          />
        )
      })}
      {media.caption && <figcaption className="text-sm">{media.caption}</figcaption>}
    </figure>
  )
}

const CaseStudySlide = ({
  caseStudy,
  joinWaitlistUrl
}: {
  caseStudy: Content.Playbook
  joinWaitlistUrl: string
}) => {
  const { isOpen: isPlaybookOpen, setIsOpen: setIsPlaybookOpen } = UI.useDialogQueryState(
    `playbook-${caseStudy.id}`
  )

  const openDialog = () => setIsPlaybookOpen(true)
  const closeDialog = () => setIsPlaybookOpen(false)

  return (
    <UI.Carousel.Slide>
      <div
        className={UI.cx(
          "grid grid-cols-1 sm:grid-cols-2 w-full rounded-xl overflow-hidden h-full",
          classForCardBorder,
          "after:rounded-xl"
        )}
      >
        <div className="bg-gray-2 flex flex-col justify-between size-full overflow-hidden">
          <div className="aspect-[4/3] flex items-center justify-center relative">
            <div className="hidden sm:block z-10 absolute h-full w-32 top-0 -right-px bg-gradient-to-r from-transparent to-gray-2" />
            <div className="hidden sm:block z-10 absolute h-full w-32 top-0 -left-px bg-gradient-to-l from-transparent to-gray-2" />
            <div className="sm:hidden z-10 absolute h-full -bottom-px -left-px -right-px bg-gradient-to-t from-gray-2 to-transparent" />
            <img
              src={caseStudy.backgroundImage}
              alt={caseStudy.artistName}
              className="hidden sm:block object-cover w-full max-h-full absolute top-1/2 -translate-y-1/2 left-0"
            />
            <img
              src={caseStudy.artistImage}
              alt={caseStudy.artistName}
              className="sm:hidden w-full object-cover max-h-full absolute top-1/2 -translate-y-1/2 left-0"
            />
          </div>
          <div className="p-6 sm:p-10 self-end flex flex-col justify-end sm:self-start items-start gap-4">
            <p className="text-sm sm:text-lg text-pretty max-w-[400px]">{caseStudy.title}</p>
            <UI.Primitive.Dialog.Root
              open={isPlaybookOpen}
              onOpenChange={(open) => {
                if (open) {
                  openDialog()
                } else {
                  closeDialog()
                }
              }}
            >
              <UI.Primitive.Dialog.Trigger
                className={UI.classForButton({ kind: "shaded", size: "sm" })}
                onClick={openDialog}
              >
                Read more
              </UI.Primitive.Dialog.Trigger>

              <UI.Primitive.Dialog.Portal>
                <UI.Primitive.Dialog.Backdrop className="fixed inset-0 bg-[rgba(0,0,0,0.8)] backdrop-blur transition-all duration-200 [&[data-ending-style]]:opacity-0 [&[data-starting-style]]:opacity-0" />
                <UI.Primitive.Dialog.Popup
                  className={UI.cx(
                    "overflow-hidden z-50 w-full max-w-[calc(100%-2rem)] h-[calc(100%-2rem)] sm:h-[80vh] sm:max-w-xl",
                    "bg-gray-1 shadow-2xl rounded-2xl border border-gray-3",
                    "gap-4 outline-none",
                    "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
                    "duration-200",
                    "data-[starting-style]:-translate-y-1/2 data-[starting-style]:scale-95 data-[starting-style]:opacity-0",
                    "data-[ending-style]:-translate-y-1/2 data-[ending-style]:scale-95 data-[ending-style]:opacity-0"
                  )}
                >
                  <UI.Primitive.Dialog.Close className="z-40 size-8 flex items-center justify-center absolute top-4 right-4 sm:top-6 sm:right-6 rounded-lg bg-grayA-3 hover:bg-grayA-5 transition-colors duration-200">
                    <UI.Icon.Close />
                  </UI.Primitive.Dialog.Close>
                  <div className="z-40 h-6 absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-1 to-transparent" />

                  <UI.ScrollArea className="h-full">
                    <div className="flex flex-col gap-5 p-2.5 sm:p-4 pb-6 sm:pb-8">
                      <PlaybookDialogContent
                        title={caseStudy.title}
                        description={caseStudy.dialogContent.description}
                        media={caseStudy.dialogContent.media}
                        tags={caseStudy.dialogContent.tags}
                        ownableStrategies={caseStudy.dialogContent.ownableStrategies}
                        whatFansAccessed={caseStudy.dialogContent.whatFansAccessed}
                        content={caseStudy.dialogContent.content}
                        joinWaitlistUrl={joinWaitlistUrl}
                      />
                    </div>
                  </UI.ScrollArea>
                </UI.Primitive.Dialog.Popup>
              </UI.Primitive.Dialog.Portal>
            </UI.Primitive.Dialog.Root>
          </div>
        </div>
        <div
          className="hidden sm:block bg-cover bg-center"
          style={{ backgroundImage: `url(${caseStudy.artistImage})` }}
        />
      </div>
    </UI.Carousel.Slide>
  )
}

const PlaybookDialogContent = ({
  title,
  description,
  media,
  tags,
  ownableStrategies,
  whatFansAccessed,
  content,
  joinWaitlistUrl
}: {
  title: string
  description: React.ReactNode
  media: Media
  tags: Array<string>
  ownableStrategies: Array<React.ReactNode>
  whatFansAccessed: Array<{ title: string; description: string }>
  content: React.ReactNode
  joinWaitlistUrl: string
}) => (
  <div>
    <div className="flex flex-col">
      <MediaComponent media={media} />
      <div className="flex items-center gap-2 pt-8 pb-3">
        {tags.map((tag) => (
          <span
            className="h-5 inline-flex flex-grow-0 gap-1 items-center justify-center text-xs whitespace-nowrap rounded-sm text-gray-11 bg-gray-4 px-1"
            key={tag}
          >
            {tag}
          </span>
        ))}
      </div>
      <h3 className="text-base mb-4 text-pretty">{title}</h3>
      <div className="text-sm flex flex-col gap-4">{description}</div>
    </div>

    <div>
      <div className="py-8 border-b border-gray-3 flex flex-col gap-8">
        {content}

        <UnorderedList title="What fans accessed" items={whatFansAccessed} />
      </div>

      <div className="py-8">
        <DashedUnorderedList title="Ownable strategies" items={ownableStrategies} />
      </div>
      <div>
        <Link to={joinWaitlistUrl} className={UI.classForButton({ size: "md", kind: "solid" })}>
          Join as an artist
        </Link>
      </div>
    </div>
  </div>
)

const DashedUnorderedList = ({ title, items }: { title: string; items: Array<React.ReactNode> }) => {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base">{title}</h3>
      <ul className="text-sm flex flex-col gap-2">
        {items.map((item, index) => (
          <li key={index} className="flex items-baseline gap-2">
            <span className="text-gray-11">—</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const UnorderedList = ({
  title,
  items
}: {
  title: string
  items: Array<{ title: string; description: React.ReactNode }>
}) => {
  return (
    <div className="flex flex-col gap-4">
      {title && <h3 className="text-base">{title}</h3>}
      <ul className="text-sm flex flex-col gap-2">
        {items.map((item, index) => (
          <li key={index}>
            <span>{item.title}</span>
            <br />
            <span className="text-gray-11">{item.description}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const Badge = ({ children }: { children: React.ReactNode }) => {
  return (
    <UI.Card padding="none" rounded="base" className="flex">
      <span className="text-xs px-1.5 py-0.5">{children}</span>
    </UI.Card>
  )
}

const FlywheelSvg = ({ className }: { className: string }) => {
  const arrowAnimation = (delay: number) => ({
    opacity: [0.2, 1, 0.2],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: "easeInOut",
      delay
    }
  })

  return (
    <svg
      width="1260"
      height="939"
      viewBox="0 0 1260 939"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <defs>
        <radialGradient
          id="paint0_radial"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(80 567) rotate(90) scale(33)"
        >
          <stop offset="0.649038" stopColor="#070707" />
          <stop offset="1" stopColor="#070707" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="paint1_radial"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(1180 377) rotate(90) scale(33)"
        >
          <stop offset="0.649038" stopColor="#070707" />
          <stop offset="1" stopColor="#070707" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="paint2_radial"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(630 812) rotate(90) scale(33)"
        >
          <stop offset="0.649038" stopColor="#070707" />
          <stop offset="1" stopColor="#070707" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="paint3_radial"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(630 127) rotate(90) scale(33)"
        >
          <stop offset="0.649038" stopColor="#070707" />
          <stop offset="1" stopColor="#070707" stopOpacity="0" />
        </radialGradient>
      </defs>

      <ellipse
        cx="629.555"
        cy="469.041"
        rx="562"
        ry="335"
        transform="rotate(-15 629.555 469.041)"
        stroke="white"
        strokeOpacity="0.17"
        fill="none"
      />

      {/* Arrow Group 1 - Left */}
      <g>
        <circle cx="80" cy="567" r="33" fill="url(#paint0_radial)" />
        <motion.path
          d="M77.0909 575.042L77.0909 576.132L76 576.132L76 575.042L77.0909 575.042ZM77.0909 575.042L77.0909 573.951L78.1818 573.951L78.1818 575.042L77.0909 575.042ZM78.1818 573.951L78.1818 572.86L79.2727 572.86L79.2727 573.951L78.1818 573.951ZM81.4545 575.042L81.4545 573.951L82.5455 573.951L82.5455 575.042L81.4545 575.042ZM80.3636 573.951L80.3636 572.86L81.4545 572.86L81.4545 573.951L80.3636 573.951ZM79.2727 572.86L79.2727 571.769L80.3636 571.769L80.3636 572.86L79.2727 572.86ZM82.5455 576.132L82.5455 575.042L83.6364 575.042L83.6364 576.132L82.5455 576.132Z"
          fill="#484848"
          animate={arrowAnimation(0)}
        />
        <motion.path
          d="M77.0909 567.678L77.0909 568.769L76 568.769L76 567.678L77.0909 567.678ZM77.0909 567.678L77.0909 566.587L78.1818 566.587L78.1818 567.678L77.0909 567.678ZM78.1818 566.587L78.1818 565.496L79.2727 565.496L79.2727 566.587L78.1818 566.587ZM81.4545 567.678L81.4545 566.587L82.5455 566.587L82.5455 567.678L81.4545 567.678ZM80.3636 566.587L80.3636 565.496L81.4545 565.496L81.4545 566.587L80.3636 566.587ZM79.2727 565.496L79.2727 564.405L80.3636 564.405L80.3636 565.496L79.2727 565.496ZM82.5455 568.769L82.5455 567.678L83.6364 567.678L83.6364 568.769L82.5455 568.769Z"
          fill="#7B7B7B"
          animate={arrowAnimation(0.25)}
        />
        <motion.path
          d="M77.0909 560.314L77.0909 561.405L76 561.405L76 560.314L77.0909 560.314ZM77.0909 560.314L77.0909 559.223L78.1818 559.223L78.1818 560.314L77.0909 560.314ZM78.1818 559.223L78.1818 558.132L79.2727 558.132L79.2727 559.223L78.1818 559.223ZM81.4545 560.314L81.4545 559.223L82.5455 559.223L82.5455 560.314L81.4545 560.314ZM80.3636 559.223L80.3636 558.132L81.4545 558.132L81.4545 559.223L80.3636 559.223ZM79.2727 558.132L79.2727 557.042L80.3636 557.042L80.3636 558.132L79.2727 558.132ZM82.5455 561.405L82.5455 560.314L83.6364 560.314L83.6364 561.405L82.5455 561.405Z"
          fill="#EEEEEE"
          animate={arrowAnimation(0.5)}
        />
      </g>

      {/* Arrow Group 2 - Right */}
      <g>
        <circle cx="1180" cy="377" r="33" fill="url(#paint1_radial)" />
        <motion.path
          d="M1182.55 370.132L1182.55 369.042L1183.64 369.042L1183.64 370.132L1182.55 370.132ZM1182.55 370.132L1182.55 371.223L1181.45 371.223L1181.45 370.132L1182.55 370.132ZM1181.45 371.223L1181.45 372.314L1180.36 372.314L1180.36 371.223L1181.45 371.223ZM1178.18 370.132L1178.18 371.223L1177.09 371.223L1177.09 370.132L1178.18 370.132ZM1179.27 371.223L1179.27 372.314L1178.18 372.314L1178.18 371.223L1179.27 371.223ZM1180.36 372.314L1180.36 373.405L1179.27 373.405L1179.27 372.314L1180.36 372.314ZM1177.09 369.042L1177.09 370.132L1176 370.132L1176 369.042L1177.09 369.042Z"
          fill="#484848"
          animate={arrowAnimation(0)}
        />
        <motion.path
          d="M1182.55 377.496L1182.55 376.405L1183.64 376.405L1183.64 377.496L1182.55 377.496ZM1182.55 377.496L1182.55 378.587L1181.45 378.587L1181.45 377.496L1182.55 377.496ZM1181.45 378.587L1181.45 379.678L1180.36 379.678L1180.36 378.587L1181.45 378.587ZM1178.18 377.496L1178.18 378.587L1177.09 378.587L1177.09 377.496L1178.18 377.496ZM1179.27 378.587L1179.27 379.678L1178.18 379.678L1178.18 378.587L1179.27 378.587ZM1180.36 379.678L1180.36 380.769L1179.27 380.769L1179.27 379.678L1180.36 379.678ZM1177.09 376.405L1177.09 377.496L1176 377.496L1176 376.405L1177.09 376.405Z"
          fill="#7B7B7B"
          animate={arrowAnimation(0.25)}
        />
        <motion.path
          d="M1182.55 384.86L1182.55 383.769L1183.64 383.769L1183.64 384.86L1182.55 384.86ZM1182.55 384.86L1182.55 385.951L1181.45 385.951L1181.45 384.86L1182.55 384.86ZM1181.45 385.951L1181.45 387.042L1180.36 387.042L1180.36 385.951L1181.45 385.951ZM1178.18 384.86L1178.18 385.951L1177.09 385.951L1177.09 384.86L1178.18 384.86ZM1179.27 385.951L1179.27 387.042L1178.18 387.042L1178.18 385.951L1179.27 385.951ZM1180.36 387.042L1180.36 388.132L1179.27 388.132L1179.27 387.042L1180.36 387.042ZM1177.09 383.769L1177.09 384.86L1176 384.86L1176 383.769L1177.09 383.769Z"
          fill="#EEEEEE"
          animate={arrowAnimation(0.5)}
        />
      </g>

      {/* Arrow Group 3 - Bottom */}
      <g>
        <circle cx="630" cy="812" r="33" fill="url(#paint2_radial)" />
        <motion.path
          d="M637.739 813.123L638.818 812.96L638.98 814.039L637.902 814.201L637.739 813.123ZM637.739 813.123L636.661 813.285L636.498 812.206L637.577 812.044L637.739 813.123ZM636.498 812.206L635.42 812.368L635.257 811.289L636.336 811.127L636.498 812.206ZM637.091 808.807L636.012 808.97L635.85 807.891L636.928 807.729L637.091 808.807ZM636.174 810.048L635.095 810.211L634.933 809.132L636.012 808.97L636.174 810.048ZM635.257 811.289L634.179 811.452L634.016 810.373L635.095 810.211L635.257 811.289ZM638.007 807.566L636.928 807.729L636.766 806.65L637.845 806.488L638.007 807.566Z"
          fill="#484848"
          animate={arrowAnimation(0)}
        />
        <motion.path
          d="M630.458 814.218L631.537 814.055L631.699 815.134L630.62 815.296L630.458 814.218ZM630.458 814.218L629.379 814.38L629.217 813.301L630.296 813.139L630.458 814.218ZM629.217 813.301L628.138 813.463L627.976 812.384L629.055 812.222L629.217 813.301ZM629.809 809.902L628.731 810.065L628.568 808.986L629.647 808.824L629.809 809.902ZM628.893 811.143L627.814 811.306L627.652 810.227L628.731 810.065L628.893 811.143ZM627.976 812.384L626.897 812.547L626.735 811.468L627.814 811.306L627.976 812.384ZM630.726 808.661L629.647 808.824L629.485 807.745L630.564 807.583L630.726 808.661Z"
          fill="#7B7B7B"
          animate={arrowAnimation(0.25)}
        />
        <motion.path
          d="M623.177 815.313L624.256 815.15L624.418 816.229L623.339 816.391L623.177 815.313ZM623.177 815.313L622.098 815.475L621.936 814.396L623.015 814.234L623.177 815.313ZM621.936 814.396L620.857 814.558L620.695 813.479L621.774 813.317L621.936 814.396ZM622.528 810.997L621.449 811.16L621.287 810.081L622.366 809.919L622.528 810.997ZM621.612 812.238L620.533 812.401L620.371 811.322L621.449 811.16L621.612 812.238ZM620.695 813.479L619.616 813.642L619.454 812.563L620.533 812.401L620.695 813.479ZM623.445 809.756L622.366 809.919L622.204 808.84L623.282 808.678L623.445 809.756Z"
          fill="#EEEEEE"
          animate={arrowAnimation(0.5)}
        />
      </g>

      {/* Arrow Group 4 - Top */}
      <g>
        <circle cx="630" cy="127" r="33" fill="url(#paint3_radial)" />
        <motion.path
          d="M621.203 125.042L620.122 125.194L619.971 124.114L621.051 123.962L621.203 125.042ZM621.203 125.042L622.283 124.89L622.435 125.971L621.354 126.122L621.203 125.042ZM622.435 125.971L623.515 125.819L623.667 126.899L622.586 127.051L622.435 125.971ZM621.81 129.363L622.89 129.212L623.042 130.292L621.961 130.444L621.81 129.363ZM622.738 128.131L623.818 127.98L623.97 129.06L622.89 129.212L622.738 128.131ZM623.667 126.899L624.747 126.748L624.899 127.828L623.818 127.98L623.667 126.899ZM620.881 130.595L621.961 130.444L622.113 131.524L621.033 131.676L620.881 130.595Z"
          fill="#484848"
          animate={arrowAnimation(0)}
        />
        <motion.path
          d="M628.496 124.018L627.415 124.17L627.264 123.089L628.344 122.938L628.496 124.018ZM628.496 124.018L629.576 123.866L629.728 124.947L628.647 125.098L628.496 124.018ZM629.728 124.947L630.808 124.795L630.96 125.875L629.879 126.027L629.728 124.947ZM629.103 128.339L630.183 128.188L630.335 129.268L629.254 129.42L629.103 128.339ZM630.031 127.107L631.111 126.956L631.263 128.036L630.183 128.188L630.031 127.107ZM630.96 125.875L632.04 125.724L632.192 126.804L631.111 126.956L630.96 125.875ZM628.174 129.571L629.254 129.42L629.406 130.5L628.326 130.652L628.174 129.571Z"
          fill="#7B7B7B"
          animate={arrowAnimation(0.25)}
        />
        <motion.path
          d="M635.787 122.994L634.706 123.146L634.555 122.066L635.635 121.914L635.787 122.994ZM635.787 122.994L636.867 122.842L637.019 123.923L635.938 124.074L635.787 122.994ZM637.019 123.923L638.099 123.771L638.251 124.851L637.17 125.003L637.019 123.923ZM636.394 127.315L637.474 127.164L637.626 128.244L636.545 128.396L636.394 127.315ZM637.322 126.083L638.402 125.932L638.554 127.012L637.474 127.164L637.322 126.083ZM638.251 124.851L639.331 124.7L639.483 125.78L638.402 125.932L638.251 124.851ZM635.465 128.547L636.545 128.396L636.697 129.476L635.617 129.628L635.465 128.547Z"
          fill="#EEEEEE"
          animate={arrowAnimation(0.5)}
        />
      </g>
    </svg>
  )
}

const FooterLink = ({ href, title }: { href: string; title: string }) => {
  return (
    <Link to={href} target="_blank" rel="noreferrer" className="hover:border-b hover:border-gray-12">
      {title}
    </Link>
  )
}

const classForCardBorder = UI.cx(
  "relative",
  "after:content-[''] after:absolute after:inset-0 after:z-10 after:bshadow after:bshadow-grayA-3 after:pointer-events-none"
)

// TODO: optimize images
namespace Assets {
  const baseUrl = "https://assets.iyk-dev.app/music/artists/marketing-page"

  export const iphoneFrame = `${baseUrl}/iphone-frame.webp`

  export const fanPasses = [
    `${baseUrl}/passes/bmth-id.webp`,
    `${baseUrl}/passes/bradleysimpson-id.webp`,
    `${baseUrl}/passes/cmat-id.webp`,
    `${baseUrl}/passes/disturbed-id.webp`,
    `${baseUrl}/passes/edsheeran-id.webp`,
    `${baseUrl}/passes/jade-id.webp`,
    `${baseUrl}/passes/jessie-id.webp`,
    `${baseUrl}/passes/jimlegxacy-id.webp`,
    `${baseUrl}/passes/jonasbrothers-id.webp`,
    `${baseUrl}/passes/teddy-id.webp`
  ]
  export const doubledFanPasses = [...fanPasses, ...fanPasses]

  export const flywheel = {
    step1: `${baseUrl}/flywheel/d_flywheel_01.webp`,
    step2: `${baseUrl}/flywheel/d_flywheel_02.webp`,
    step3: `${baseUrl}/flywheel/d_flywheel_03.webp`,
    step4: `${baseUrl}/flywheel/d_flywheel_04.webp`
  }

  export const coreFeatures = {
    gridBg: `${baseUrl}/core-features/grid.webp`,
    chippedItems: {
      desktop: `${baseUrl}/core-features/d_core_features_1.webp`,
      mobile: `${baseUrl}/core-features/m_core_features_1.webp`
    },
    showCheckIns: {
      desktop: `${baseUrl}/core-features/d_core_features_2.webp`,
      mobile: `${baseUrl}/core-features/m_core_features_2.webp`
    },
    proofsAndUnlocks: {
      desktop: `${baseUrl}/core-features/d_core_features_3.png`,
      mobile: `${baseUrl}/core-features/m_core_features_3.webp`
    },
    worldPass: {
      desktop: `${baseUrl}/core-features/d_core_features_4.webp`,
      mobile: `${baseUrl}/core-features/m_core_features_4.webp`
    }
  }

  export const artistMarquee = [
    `${baseUrl}/artist-cards/9lives.png`,
    `${baseUrl}/artist-cards/aitch.png`,
    `${baseUrl}/artist-cards/arden-jones.png`,
    `${baseUrl}/artist-cards/ari-lennox.png`,
    `${baseUrl}/artist-cards/black-honey.png`,
    `${baseUrl}/artist-cards/bmth.png`,
    `${baseUrl}/artist-cards/bradley-simpson.png`,
    `${baseUrl}/artist-cards/cat-burns.png`,
    `${baseUrl}/artist-cards/chris-lake.png`,
    `${baseUrl}/artist-cards/chromeo.png`,
    `${baseUrl}/artist-cards/cmat.png`,
    `${baseUrl}/artist-cards/ctr.png`,
    `${baseUrl}/artist-cards/dallas-smith.png`,
    `${baseUrl}/artist-cards/daniel-allan.png`,
    `${baseUrl}/artist-cards/disturbed.png`,
    `${baseUrl}/artist-cards/dongwoo.png`,
    `${baseUrl}/artist-cards/ed-sheeran.png`,
    `${baseUrl}/artist-cards/elephante.png`,
    `${baseUrl}/artist-cards/flyana-boss.png`,
    `${baseUrl}/artist-cards/grimes.png`,
    `${baseUrl}/artist-cards/gryffin.png`,
    `${baseUrl}/artist-cards/gunna.png`,
    `${baseUrl}/artist-cards/henry-moodie.png`,
    `${baseUrl}/artist-cards/jade.png`,
    `${baseUrl}/artist-cards/jennie-1.png`,
    `${baseUrl}/artist-cards/jennie.png`,
    `${baseUrl}/artist-cards/jessie-reyez.png`,
    `${baseUrl}/artist-cards/jim-legxacy.png`,
    `${baseUrl}/artist-cards/jonas-brothers.png`,
    `${baseUrl}/artist-cards/juice-wrld.png`,
    `${baseUrl}/artist-cards/kevin-gates.png`,
    `${baseUrl}/artist-cards/kwes.png`,
    `${baseUrl}/artist-cards/lane8.png`,
    `${baseUrl}/artist-cards/latasha.png`,
    `${baseUrl}/artist-cards/lil-tecca.png`,
    `${baseUrl}/artist-cards/michelle.png`,
    `${baseUrl}/artist-cards/nectar-woode.png`,
    `${baseUrl}/artist-cards/ozomatli.png`,
    `${baseUrl}/artist-cards/peter-doherty.png`,
    `${baseUrl}/artist-cards/rats.png`,
    `${baseUrl}/artist-cards/red-rum-club.png`,
    `${baseUrl}/artist-cards/slander.png`,
    `${baseUrl}/artist-cards/tate-mcrae.png`,
    `${baseUrl}/artist-cards/teddy-swims.png`,
    `${baseUrl}/artist-cards/the-wombats.png`,
    `${baseUrl}/artist-cards/tom-grennan.png`,
    `${baseUrl}/artist-cards/trivium.png`,
    `${baseUrl}/artist-cards/verite.png`,
    `${baseUrl}/artist-cards/wizthemc.png`,
    `${baseUrl}/artist-cards/yerin.png`,
    `${baseUrl}/artist-cards/yonaka.png`,
    `${baseUrl}/artist-cards/young-jae.png`
  ]

  export const playbook = {
    "chance-the-rapper": {
      artist: `${baseUrl}/powered-by-iyk/ctr/artist.webp`,
      background: `${baseUrl}/powered-by-iyk/ctr/background.png`,
      content: {
        hero: `${baseUrl}/powered-by-iyk/ctr/content-hero.webp`,
        item1: `${baseUrl}/powered-by-iyk/ctr/content-01.webp`,
        item2: `${baseUrl}/powered-by-iyk/ctr/content-02.mp4`,
        item2Poster: `${baseUrl}/powered-by-iyk/ctr/content-02-poster.webp`
      }
    },
    "ed-sheeran": {
      artist: `${baseUrl}/powered-by-iyk/ed/artist.webp`,
      background: `${baseUrl}/powered-by-iyk/ed/background.png`,
      content: {
        hero: `${baseUrl}/powered-by-iyk/ed/hero.mp4`,
        heroPoster: `${baseUrl}/powered-by-iyk/ed/hero-poster.png`,
        item1: `${baseUrl}/powered-by-iyk/ed/content-01.mp4`,
        item1Poster: `${baseUrl}/powered-by-iyk/ed/content-01-poster.png`,
        item2: `${baseUrl}/powered-by-iyk/ed/content-02.png`,
        item3: `${baseUrl}/powered-by-iyk/ed/content-03.webp`
      }
    },
    disturbed: {
      artist: `${baseUrl}/powered-by-iyk/disturbed/artist.webp`,
      background: `${baseUrl}/powered-by-iyk/disturbed/background.png`,
      content: {
        hero: `${baseUrl}/powered-by-iyk/disturbed/content-hero.webp`,
        item1: `${baseUrl}/powered-by-iyk/disturbed/content-01.webp`,
        item2: `${baseUrl}/powered-by-iyk/disturbed/content-02.webp`
      }
    },
    "teddy-swims": {
      artist: `${baseUrl}/powered-by-iyk/teddy-swims/artist.webp`,
      background: `${baseUrl}/powered-by-iyk/teddy-swims/background.webp`,
      content: {
        hero: `${baseUrl}/powered-by-iyk/teddy-swims/content-hero.webp`,
        item1: `${baseUrl}/powered-by-iyk/teddy-swims/content-01.webp`
      }
    }
  }

  export const shop = {
    banner: `${baseUrl}/shop/banner.webp`,
    wristband: `${baseUrl}/shop/wristband.png`,
    apparel: `${baseUrl}/shop/apparel.png`,
    chippedStickers: `${baseUrl}/shop/stickers.png`,
    tapCard: `${baseUrl}/shop/tapcard.png`,
    lanyard: `${baseUrl}/shop/lanyard.png`
  }

  export const subFeatures = {
    buildYourWorld: `${baseUrl}/sub-features/d_build_your_world.webp`,
    buildYourWorldMobile: `${baseUrl}/sub-features/m_build_your_world.webp`,
    insights: `${baseUrl}/sub-features/sub_features_01.webp`,
    tiersAndRanks: `${baseUrl}/sub-features/sub_features_02.webp`,
    fanClips: `${baseUrl}/sub-features/sub_features_03.webp`,
    quests: `${baseUrl}/sub-features/sub_features_04.webp`
  }
}

namespace Content {
  export const flywheel = [
    {
      step: 1,
      title: "Activate your merch and shows",
      image: Assets.flywheel.step1,
      description:
        "Embed IYK Chips into your merch and add IYK Check-ins at your shows to create direct connections and experiences for your IRL fans."
    },
    {
      step: 2,
      title: "Verify fan participation",
      image: Assets.flywheel.step2,
      description:
        "Fans collect a Proof for every item claimed and show attended, creating a verified record of who consistently shows up."
    },
    {
      step: 3,
      title: "Reward your fans",
      image: Assets.flywheel.step3,
      description:
        "Each Proof grants access to Unlocks. Create exclusive rewards for your biggest fans in a whole new way - powered by real, verifiable actions."
    },
    {
      step: 4,
      title: "Grow fan loyalty",
      image: Assets.flywheel.step4,
      description:
        "Fans continue to get recognized and level up their digital identity through IRL activity, fuelling engagement for the next show, drop, or release."
    }
  ]

  export const subFeatures = [
    {
      title: "Insights",
      description:
        "Capture valuable first-party data and translate IRL fan activity into meaningful fan insights. ",
      image: Assets.subFeatures.insights,
      isComingSoon: false
    },
    {
      title: "Tiers & Ranks",
      description:
        "XP and Rankings reveal who's most engaged, helping you identify the supporters driving your world forward.",
      image: Assets.subFeatures.tiersAndRanks,
      isComingSoon: false
    },
    {
      title: "Fan clips",
      description: "Own and showcase the footage your fans capture on tour, all curated in one place.",
      image: Assets.subFeatures.fanClips,
      isComingSoon: true
    },
    {
      title: "Quests",
      description: "Fans take on guided challenges that build XP and turn engagement into real progression.",
      image: Assets.subFeatures.quests,
      isComingSoon: true
    }
  ]

  export const coreFeatures = [
    {
      title: "Recognize your Merch Owners",
      description:
        "Every merch item becomes a fan-engagement touchpoint. With a digital layer, your merch goes beyond just a piece of fabric—becoming an ongoing channel to identify owners, engage fans, and deepen connection.",
      pill: "Chipped Items",
      desktopImage: Assets.coreFeatures.chippedItems.desktop,
      mobileImage: Assets.coreFeatures.chippedItems.mobile
    },
    {
      title: "Identify the Fans Who Show Up",
      description:
        "Every show becomes an opportunity for fans to level up. As an artist, see who's showing up and boost post-show engagement with show-specific rewards.",
      pill: "Show Check-Ins",
      desktopImage: Assets.coreFeatures.showCheckIns.desktop,
      mobileImage: Assets.coreFeatures.showCheckIns.mobile
    },
    {
      title: "Reward Your Real Fans",
      description:
        "When fans complete verified actions, they earn a Proof tied to that action. Proofs give them immediate access to Unlocks: curated, gated experiences such as early access and exclusive content.",
      pill: "proofs & Unlocks",
      desktopImage: Assets.coreFeatures.proofsAndUnlocks.desktop,
      mobileImage: Assets.coreFeatures.proofsAndUnlocks.mobile
    },
    {
      title: "The Living Fan Identity",
      description:
        "Create a direct artist-to-fan connection by turning ownership and participation into a shareable living ID. As fans engage through merch and shows, their World ID evolves alongside them.",
      pill: "World ID",
      desktopImage: Assets.coreFeatures.worldPass.desktop,
      mobileImage: Assets.coreFeatures.worldPass.mobile
    }
  ]

  export const shop = {
    products: [
      {
        title: "Lanyard",
        image: Assets.shop.lanyard,
        hideOnMobile: false
      },
      {
        title: "Wristband",
        image: Assets.shop.wristband,
        hideOnMobile: true
      },
      {
        title: "Apparel",
        image: Assets.shop.apparel,
        hideOnMobile: false
      },
      {
        title: "Chipped Stickers",
        image: Assets.shop.chippedStickers,
        hideOnMobile: false
      },
      {
        title: "TapCard",
        image: Assets.shop.tapCard,
        hideOnMobile: false
      }
    ]
  }

  export type Playbook = {
    id: string
    title: string
    artistImage: string
    artistName: string
    backgroundImage: string
    dialogContent: {
      description: React.ReactNode
      media: Media
      tags: Array<string>
      whatFansAccessed: Array<{ title: string; description: string }>
      ownableStrategies: Array<React.ReactNode>
      content: React.ReactNode
    }
  }

  export const playbookStories: Array<Playbook> = [
    {
      id: "chance-the-rapper",
      title: "How Chance the Rapper Elevated Merch For His Album Rollout",
      artistImage: Assets.playbook["chance-the-rapper"].artist,
      artistName: "Chance the Rapper",
      backgroundImage: Assets.playbook["chance-the-rapper"].background,
      dialogContent: {
        description: (
          <>
            <p>
              Chance didn't treat merch as an add-on to his latest album release. He turned his entire Star
              Line Shipping Co. collection into an ecosystem of layered entry points. All 20 merch items were
              chipped, each powered by IYK's NFC technology.
            </p>
            <p>
              A tap from a phone verified the items and pulled fans into the Star Line universe, where new
              music and moments surfaced over time.
            </p>
          </>
        ),
        media: Media.Image({
          id: "chance-the-rapper-innovative-strategy-to-chipped-merch",
          src: Assets.playbook["chance-the-rapper"].content.hero,
          alt: "Chance the Rapper's innovative strategy to chipped merch",
          width: 511,
          height: 288
        }),
        tags: ["Chipped merch"],
        whatFansAccessed: [
          {
            title: "Behind-the-scenes studio sessions",
            description:
              "Recordings that revealed how the music was made, available only through chipped items or a fan's profile."
          },
          {
            title: "Exclusive lyric video",
            description:
              "A visual interpretation of Star Line lyrics that debuted exclusively for fans who tapped their merch, not available anywhere else."
          },
          {
            title: "Star Line album & other exclusive audio",
            description:
              "Early access to the full album along with unreleased tracks and audio drops that lived only in fans' possession, tied directly to their claimed items."
          }
        ],
        ownableStrategies: [
          "Treat every item in a collection as a membership card into your world.",
          "Use chipped merch to stage a rolling narrative: demos, sessions, stems, live cuts, etc.",
          "Drive both charting and a direct-to-fan channel with chipped merch"
        ],
        content: (
          <>
            <MediaComponent
              media={Media.Video({
                id: "chance-the-rapper-innovative-strategy-to-chipped-merch",
                playbackUrl: Assets.playbook["chance-the-rapper"].content.item2,
                posterUrl: Assets.playbook["chance-the-rapper"].content.item2Poster,
                width: 1022,
                height: 578
              })}
            />
            <p className="text-2xl text-balance my-2">
              By chipping the full collection, Chance turned merch ownership into a persistent access point to
              his latest album.
            </p>
            <MediaComponent
              media={Media.Image({
                id: "chance-the-rapper-innovative-strategy-to-chipped-merch",
                src: Assets.playbook["chance-the-rapper"].content.item1,
                alt: "Chance the Rapper's innovative strategy to chipped merch",
                width: 511,
                height: 288
              })}
            />
          </>
        )
      }
    },
    {
      id: "ed-sheeran",
      title: "Inside Ed Sheeran's Chart-eligible NFC Album Product",
      artistImage: Assets.playbook["ed-sheeran"].artist,
      artistName: "Ed Sheeran",
      backgroundImage: Assets.playbook["ed-sheeran"].background,
      dialogContent: {
        description: (
          <>
            <p>
              For his three homecoming shows in Ipswich, Ed Sheeran released the chipped Play x Portman Road
              Commemorative Lanyard that elevated a simple keepsake into a smart, and functional album format.
            </p>
            <p>
              Sold at the stadium and later in his official store, fans could tap to access exclusive content
              and the full 'Play' album, along with track previews before broader public release.
            </p>
          </>
        ),
        media: Media.Video({
          id: "ed-sheeran-chart-eligible-nfc-album-product",
          playbackUrl: Assets.playbook["ed-sheeran"].content.hero,
          posterUrl: Assets.playbook["ed-sheeran"].content.heroPoster,
          width: 1920,
          height: 1080
        }),
        tags: ["Charting Product", "Chipped merch"],
        whatFansAccessed: [
          {
            title: "'Play' the album",
            description:
              'Fans were given an early preview of "Opening" and "A Little More", along with the full album playback when \'Play\' was officially released.'
          },
          {
            title: "BTS Show Content",
            description:
              "Behind-the-scenes video and photo content from soundcheck and backstage at Portman Road, accessed exclusively to fans who verified attendance. "
          },
          {
            title: "Live Video (Exclusive on IYK)",
            description:
              "After the shows, the lanyard also allowed fans to relive the show with a performance of Sapphire, live from Ipswich. "
          }
        ],
        ownableStrategies: [
          "Turn venue-specific merch (lanyards, wristbands, laminates) into a smart album format that counts toward charts and is not just a souvenir",
          "Use IYK to bridge pre-release, release week, and tour content into a single product",
          "Tie chart-eligible downloads to verified ownership, so physical sales and in-person attendance become a measurable, direct-to-fan channel you can fully access"
        ],
        content: (
          <>
            <MediaComponent
              media={Media.Video({
                id: "ed-sheeran-chart-eligible-nfc-album-product",
                playbackUrl: Assets.playbook["ed-sheeran"].content.item1,
                posterUrl: Assets.playbook["ed-sheeran"].content.item1Poster,
                caption:
                  "Every lanyard tapped became both a fan moment and a chart move. IYK engineered a new album format, turning first time taps into chart-eligible album downloads, reported the Official Charts Company. ",
                width: 1920,
                height: 1080
              })}
            />
            <MediaComponent
              media={Media.Image({
                id: "ed-sheeran-chart-eligible-nfc-album-product",
                alt: "Official most downloaded British album of 2025",
                src: Assets.playbook["ed-sheeran"].content.item3,
                width: 770,
                height: 433
              })}
            />
            <p className="text-2xl text-balance my-2">
              The Lanyard drove 67% of all first-week album downloads, contributing to the "Official Most
              Downloaded British Album of 2025".
            </p>
            <MediaComponent
              media={Media.Image({
                id: "ed-sheeran-chart-eligible-nfc-album-product-item2",
                src: Assets.playbook["ed-sheeran"].content.item2,
                alt: "Ed Sheeran's chart-eligible NFC album product",
                width: 1026,
                height: 578
              })}
            />
          </>
        )
      }
    },
    {
      id: "disturbed",
      title: "Disturbed Used Check-ins to Build Direct Connections to over 100,000 Fans on Tour",
      artistImage: Assets.playbook["disturbed"].artist,
      artistName: "Disturbed",
      backgroundImage: Assets.playbook["disturbed"].background,
      dialogContent: {
        media: Media.Image({
          id: "disturbed-check-ins",
          src: Assets.playbook["disturbed"].content.hero,
          alt: "Disturbed's check-ins",
          width: 1022,
          height: 578
        }),
        tags: ["Charting Product", "Chipped Merch"],
        description:
          "During The Sickness 25th Anniversary Tour, Disturbed captured fan data from thousands of show attendees across 13 countries. Fans checked in via geo-gated QR codes on stage and venue screens, collecting a 'Proof' that verified their attendance and unlocked exclusive content and rewards.",
        whatFansAccessed: [
          {
            title: "Seat Upgrades",
            description:
              "After checking in, fans automatically entered a sweepstakes for a chance to get upgraded to the best seat at the show."
          },
          {
            title: "Exclusive Content",
            description:
              "Fans accessed exclusive content such as greetings from the band and show-specific photo galleries from the fan's city."
          },
          {
            title: "Fan Clips",
            description:
              "Fans submitted their authentic moments from the show, giving both the artist and fans access to a curated feed of tour content."
          },
          {
            title: "Signed Guitar",
            description:
              "Each leg of the tour: US and EU/UK, offered a grand prize of a signed guitar, with all checked-in attendees automatically entered to win."
          }
        ],
        ownableStrategies: [
          "Turn anonymous show-goers into verified fans",
          "Follow up with attendees after each show with tailored offers or content",
          "Turn every show into a gateway for your fan community to keep engaging",
          "Re-engage attendees with early pre-sale access to the next show"
        ],
        content: (
          <>
            <MediaComponent
              media={Media.Image({
                id: "disturbed-check-ins",
                src: Assets.playbook["disturbed"].content.item1,
                alt: "Disturbed's check-ins",
                width: 1022,
                height: 578
              })}
            />
            <p className="text-2xl text-balance my-2 max-w-[400px]">
              Of the 100,000+ fans identified, 83% were completely new to Disturbed's mailing list.
            </p>
            <UnorderedList
              title=""
              items={[
                {
                  title: "Check-ins That Power Insights:",
                  description:
                    "Every check-in is added to a comprehensive insights dashboard for Disturbed's team to view. Each new check-in captured valuable first-party fan data, including email, phone number, shows attended, and chipped merch claimed."
                },
                {
                  title: "Fan Experiences Beyond the Show:",
                  description:
                    "Each fan profile created through IYK-powered check-ins became the foundation for ongoing engagement. Disturbed offered post-show 'Unlocks' to fans who checked in that lasted beyond the event."
                }
              ]}
            />
            <MediaComponent
              media={Media.Image({
                id: "disturbed-seat-upgrades",
                src: Assets.playbook["disturbed"].content.item2,
                alt: "Disturbed's seat upgrades",
                width: 1022,
                height: 578
              })}
            />
          </>
        )
      }
    },
    {
      id: "teddy-swims",
      title: "How Teddy Swims Used TapCards to Deepen VIP Value",
      artistImage: Assets.playbook["teddy-swims"].artist,
      artistName: "Teddy Swims",
      backgroundImage: Assets.playbook["teddy-swims"].background,
      dialogContent: {
        media: Media.Image({
          id: "teddy-swims-tap-cards",
          src: Assets.playbook["teddy-swims"].content.hero,
          alt: "Teddy Swims' TapCards",
          width: 1022,
          height: 578
        }),
        tags: ["VIP", "Chipped Merch"],
        description:
          "Teddy Swims' VIP package included a tour-branded TapCard (produced by ATC) that transformed a collectible physical item into a digital fan engagement touchpoint. As the tour progressed, it delivered updated behind-the-scenes photos, videos, and audio straight from Teddy that only his VIP fans could access.",
        whatFansAccessed: [
          {
            title: "BTS Tour Galleries",
            description:
              "40+ behind-the-scenes photos from the tour across North America and Europe, available only to those who own the TapCard."
          },
          {
            title: "Personalized Videos",
            description:
              "Direct messages from Teddy provided top-tier fans with a more personal, exclusive experience."
          },
          {
            title: "Acoustic Performances",
            description:
              "New content rolled out over time, prompting fans to tap in again to unlock never-before-seen performances."
          }
        ],
        ownableStrategies: [
          "Create an exclusive tour-branded TapCard that can be included in your VIP package",
          "Turn VIP from a one-night upgrade into an ongoing experience by continuing to update content during and after the tour",
          "Share personalized video messages exclusive for VIPs to offer unique value to your biggest supporters",
          "Re-engage VIPs in the future with early pre-sale access to the next tour"
        ],
        content: (
          <>
            <p className="text-2xl text-balance my-2 max-w-[400px]">
              Over 13,000 fans received tour-branded TapCards in their VIP package.
            </p>
            <UnorderedList
              title=""
              items={[
                {
                  title: "VIP Designed For Repeat Engagement:",
                  description:
                    "The TapCard became a digi-physical keepsake fans could proudly display and tap to access exclusive content throughout the tour. This turned the VIP package into an experience that fans continued to engage with beyond the show."
                },
                {
                  title: "VIP Products Enhanced With a Digital Layer:",
                  description:
                    "Alongside more traditional VIP items like a lanyard and tour shirt, the TapCard stood out amongst static products, delivering an extra digital layer of value that fans couldn't find anywhere else."
                },
                {
                  title: "Direct Content Channel: Only for VIPs:",
                  description:
                    "The content on each TapCard can only be viewed when the fan physically has the card in hand, ensuring that only verified VIPs can access the exclusive moments Teddy intended just for them."
                }
              ]}
            />
            <MediaComponent
              media={Media.Image({
                id: "teddy-swims-exclusive-moments",
                src: Assets.playbook["teddy-swims"].content.item1,
                alt: "Teddy Swims' exclusive moments",
                width: 1022,
                height: 578
              })}
            />
          </>
        )
      }
    }
  ]

  export const footer = {
    urls: {
      support: "mailto:contact@iyk.app",
      twitter: "https://x.com/iyk_app",
      instagram: "https://www.instagram.com/iyk_app/",
      privacyPolicy: "https://assets.iyk.app/iyk-privacy-policy.pdf",
      termsOfService: "https://assets.iyk.app/iyk-terms-of-service.pdf"
    }
  }
}
